/* C.5 Phase 2 — Coverage Report.

   Bound to GET /api/data/admin/coverage-report. Surfaces the
   real-vs-placeholder logo coverage classification so operators can
   prioritise which entities to curate up.

   Real sources (whitelist): apple-touch-icon, favicon-svg, google-s2,
   google-s2-64, duckduckgo, manual-curated, wikipedia, manual_upload.
   Placeholder source: generated-letter-mark.

   Layout:
     - 4 summary cards: total, real, placeholder, missing
     - "Top sources" bar listing (sorted by count desc)
   This is the v1 surface — the per-tier × per-source matrix is
   intentionally NOT rendered here to keep the page scannable. */

import { useEffect, useState } from "react";
import { Image, ImageBroken, ImageSquare, Hash, DownloadSimple } from "@phosphor-icons/react";
import { api, API_BASE, getToken, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, pct, accent, testId }) {
    return (
        <div data-testid={testId} className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 text-slate-500 mb-3">
                <Icon size={16} className={accent} />
                <span className="reedos-eyebrow">{label}</span>
            </div>
            <div className="text-3xl font-display font-bold text-slate-900 tabular-nums" data-testid={`${testId}-value`}>
                {value.toLocaleString()}
            </div>
            {pct !== undefined && (
                <div className="text-xs text-slate-500 mt-1" data-testid={`${testId}-pct`}>
                    {pct.toFixed(2)}% of corpus
                </div>
            )}
        </div>
    );
}

function SourceBar({ source, count, max, isReal, isPlaceholder }) {
    const pct = max > 0 ? (count / max) * 100 : 0;
    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
                <span className="font-mono text-xs text-slate-700 flex items-center gap-2">
                    {source}
                    {isPlaceholder && (
                        <span className="text-[9px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                            placeholder
                        </span>
                    )}
                    {isReal && (
                        <span className="text-[9px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                            real
                        </span>
                    )}
                </span>
                <span className="font-mono tabular-nums text-slate-600">{count.toLocaleString()}</span>
            </div>
            <div className="h-1.5 rounded bg-slate-100 overflow-hidden">
                <div
                    className={
                        isPlaceholder ? "h-full bg-amber-400" :
                        isReal ? "h-full bg-emerald-500" :
                        "h-full bg-slate-400"
                    }
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

export default function CompanyCoverage() {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const { can } = useAuth();

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/data/admin/coverage-report");
            setReport(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load coverage"));
        } finally {
            setLoading(false);
        }
    };

    /* Phase 2.1 — Repository bundle download.
       Hits the backend's existing GET /api/data/admin/export endpoint
       (auth-required, data:export perm). We bypass axios so we can stream
       the ZIP straight to a Blob — axios would JSON-parse on its own. */
    const downloadBundle = async () => {
        setDownloading(true);
        try {
            const resp = await fetch(`${API_BASE}/data/admin/export`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!resp.ok) {
                const detail = await resp.text();
                throw new Error(`HTTP ${resp.status} — ${detail.slice(0, 200)}`);
            }
            const blob = await resp.blob();
            const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
            const filename = `reedos-companies-repo-${ts}.zip`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(
                `Downloaded ${filename} (${(blob.size / (1024 * 1024)).toFixed(1)} MB)`
            );
        } catch (err) {
            toast.error(`Failed to download repository bundle: ${err.message}`);
        } finally {
            setDownloading(false);
        }
    };

    useEffect(() => { load(); }, []);

    if (loading || !report) {
        return (
            <div data-testid="coverage-loading" className="p-12 text-center text-slate-400">
                Loading coverage…
            </div>
        );
    }

    const realSet = new Set(report.real_sources || []);
    const placeholderSet = new Set(report.placeholder_sources || []);
    const entries = Object.entries(report.by_source || {})
        .sort((a, b) => b[1] - a[1]);
    const max = entries.length ? entries[0][1] : 0;

    return (
        <div data-testid="company-coverage-page">
            <PageHeader
                eyebrow="Data · Company Repository"
                title="Coverage Report"
                description={`Generated ${report.generated_at}. ${report.total_records.toLocaleString()} entities, ${report.total_visual_coverage.percent}% visual coverage.`}
                actions={
                    <div className="flex items-center gap-2">
                        {can("data:export") && (
                            <Button
                                size="sm"
                                onClick={downloadBundle}
                                disabled={downloading}
                                data-testid="coverage-download-bundle-btn"
                                className="gap-2"
                            >
                                <DownloadSimple size={16} weight="bold" />
                                {downloading ? "Preparing…" : "Download Repository Bundle"}
                            </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={load} data-testid="coverage-refresh-btn">
                            Refresh
                        </Button>
                    </div>
                }
            />

            <div className="px-6 sm:px-8 py-6 space-y-6">
                {/* Summary cards.
                    real + placeholder + unclassified + missing always
                    sums to total — the 5-bucket model surfaces 'logo
                    present but source label not recorded' as an
                    explicit category rather than letting it inflate
                    'missing' (which would be misleading because the
                    binary IS on disk). The unclassified card is
                    rendered only when the count is non-zero to keep
                    the visual hierarchy crisp on a fully-curated
                    corpus. */}
                <div className={`grid grid-cols-1 sm:grid-cols-2 ${(report.unclassified_coverage?.count || 0) > 0 ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-4`}>
                    <StatCard
                        icon={Hash}
                        label="Total records"
                        value={report.total_records}
                        accent="text-slate-500"
                        testId="card-total"
                    />
                    <StatCard
                        icon={Image}
                        label="Real visual marks"
                        value={report.real_logo_coverage.count}
                        pct={report.real_logo_coverage.percent}
                        accent="text-emerald-600"
                        testId="card-real"
                    />
                    <StatCard
                        icon={ImageSquare}
                        label="Letter-mark placeholders"
                        value={report.placeholder_coverage.count}
                        pct={report.placeholder_coverage.percent}
                        accent="text-amber-600"
                        testId="card-placeholder"
                    />
                    {(report.unclassified_coverage?.count || 0) > 0 && (
                        <StatCard
                            icon={ImageSquare}
                            label="Unclassified source"
                            value={report.unclassified_coverage.count}
                            pct={report.unclassified_coverage.percent}
                            accent="text-slate-500"
                            testId="card-unclassified"
                        />
                    )}
                    <StatCard
                        icon={ImageBroken}
                        label="No logo set"
                        value={report.missing.count}
                        pct={report.missing.percent}
                        accent="text-rose-500"
                        testId="card-missing"
                    />
                </div>

                {/* Source breakdown */}
                <div className="rounded-lg border border-slate-200 bg-white p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display text-lg font-bold text-slate-900">Source distribution</h2>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                            <span className="flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-full bg-emerald-500" /> real
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-full bg-amber-400" /> placeholder
                            </span>
                        </div>
                    </div>
                    <div className="space-y-4" data-testid="source-bars">
                        {entries.length === 0 && (
                            <div className="text-sm text-slate-400">No source data yet — run an import or upload some logos to see coverage.</div>
                        )}
                        {entries.map(([source, count]) => (
                            <SourceBar
                                key={source}
                                source={source}
                                count={count}
                                max={max}
                                isReal={realSet.has(source)}
                                isPlaceholder={placeholderSet.has(source)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
